import { Loader } from '@/components/ui/loader';
import { GlobalLoader } from './ui/GlobalLoader';
import { Heart, MessageCircle, MoreHorizontal, Repeat, FileText, Trash2, Archive, ArchiveRestore, Share, Star, ChevronDown, Tag, Share2, Eye, ArrowBigUp, Globe } from 'lucide-react';
import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { processUsernames } from '../lib/utils';
import { Post } from '../types';
import { MediaRenderer, getMediaInfo } from './MediaRenderer';
import { ThreadPopup } from './ThreadPopup';
import { FullPostPopup } from './FullPostPopup';
import { HashtagDisplay } from './ui/hashtag-display';
import { NoteModal } from './NoteModal';
import { DeleteConfirmDialog } from './DeleteConfirmDialog';
import { CategoryChangePopup } from './CategoryChangePopup';
import { LinkedInPollComponent } from './LinkedInPollComponent';
import { LinkedInArticleComponent } from './LinkedInArticleComponent';
import {
  parseHtmlEntities,
  formatNumber
} from '../lib/postUtils';
import { usePostActions } from '../hooks/usePostActions';
import { getPostDisplayData, handlePostClickAction, getFavouriteState } from '../utils/postActions';
import { trackPostCategorized } from '../utils/eventTracking';
import { Category } from '@/services/api';
import { useImageProxy } from '../hooks/useImageProxy';
import { Checkbox } from './ui/checkbox';

// Component for profile image with proxy support (proxy API is Instagram-only)
const ProfileImage = ({ url, alt, postId, platform, className }: { url: string | null | undefined; alt: string; postId: number; platform?: string | null; className?: string }) => {
  const proxiedUrl = useImageProxy(url, postId, 'profile_picture', platform);
  return (
    <img
      src={proxiedUrl ?? "/assets/placeholder.jpg"}
      alt={alt}
      className={className}
      onError={(e) => {
        e.currentTarget.src = "/assets/placeholder.jpg";
      }}
    />
  );
};

interface ListViewProps {
  reels: Post[];
  loading: boolean;
  initialLoading: boolean;
  isBlurred: boolean;
  onLoadMore: () => void;
  hasMore: boolean;
  onNoteUpdate?: (postId: number, note: string) => void;
  onCategoryUpdate?: (postId: number, category?: any) => void;
  onPostDelete?: (postId: number) => void;
  onPostArchive?: (postId: number) => void;
  favouriteStates?: { [key: string]: boolean };
  onFavouriteToggle?: (postId: number, isFavourite: boolean) => void;
  categories: Category[];
  /** When true, show full caption without truncation (e.g. on post detail page) */
  showFullCaption?: boolean;
  selectionMode?: boolean;
  selectedPostIds?: Set<number>;
  onToggleSelect?: (postId: number, shiftKey?: boolean) => void;
}

const FaviconImage = ({ src, alt }: { src: string; alt: string }) => {
  const [useFallback, setUseFallback] = useState(false);
  if (useFallback) {
    return (
      <div className="w-12 h-12 rounded-full bg-gray-700 flex-shrink-0 flex items-center justify-center">
        <Globe className="w-6 h-6 text-gray-400" />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      className="w-12 h-12 rounded-full bg-gray-700 flex-shrink-0 object-cover"
      onLoad={(e) => {
        if (e.currentTarget.naturalWidth <= 16) setUseFallback(true);
      }}
      onError={() => setUseFallback(true)}
    />
  );
};

// Helper component to render text with clickable usernames
const ProcessedText = ({ text, platform }: { text: string, platform: 'INSTAGRAM' | 'X' | 'LINKEDIN' | 'TIKTOK' | 'REDDIT' | 'CHROME_BOOKMARK' }) => {
  const processedParts = processUsernames(text, platform);

  return (
    <>
      {processedParts.map((part, index) => {
        if (part.type === 'username') {
          return (
            <a
              key={`username-${index}`}
              href={part.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-purple-600 hover:text-purple-400 transition-colors cursor-pointer"
              onClick={(e) => e.stopPropagation()}
            >
              {part.content}
            </a>
          );
        } else {
          return part.content;
        }
      })}
    </>
  );
};





// Component for displaying quoted tweets
const QuotedTweet = ({ quotedTweet }: { quotedTweet: any }) => {
  // Helper function to format content with proper line breaks (for quoted tweets, don't filter empty lines)
  const formatQuotedContent = (content: string) => {
    if (!content) return '';
    const parsedContent = parseHtmlEntities(content);
    const lines = parsedContent.split('\n');
    return lines.join('\n');
  };

  const quotedMediaInfo = useMemo(() => getMediaInfo(quotedTweet), [quotedTweet]);

  // Add defensive checks for undefined author or user
  const author = quotedTweet?.author;
  const user = author?.user;
  const userName = user?.name || "Unknown User";
  const userUsername = user?.username || "unknown";
  const userProfileImage = user?.profile_image || "/assets/placeholder.jpg";

  return (
    <div className="border border-gray-600 rounded-lg p-3 my-3 bg-gray-800/50">
      <div className="flex items-start space-x-2 mb-2">
        <img
          src={userProfileImage}
          alt={userName}
          className="w-10 h-10 rounded-full"
          onError={(e) => {
            e.currentTarget.src = "/assets/placeholder.jpg";
          }}
        />
        <div>
          <div className="flex flex-col space-y-1">
            <span className="font-medium text-white text-sm">{userName}</span>
            <span className="text-gray-400 text-sm">@{userUsername}</span>
          </div>
        </div>
      </div>

      <div className="mb-2">
        <p className="text-white text-sm leading-relaxed whitespace-pre-line">
          {formatQuotedContent(quotedTweet.content)}
        </p>
      </div>

      {quotedTweet.media_urls && quotedTweet.media_urls.length > 0 && (
        <div className="mb-2">
          <MediaRenderer
            mediaInfo={quotedMediaInfo}
            maxHeight="200px"
            className="w-full"
          />
        </div>
      )}
    </div>
  );
};

// Component for displaying quoted LinkedIn posts
const QuotedLinkedInPost = ({ quotedPost }: { quotedPost: any }) => {
  // Helper function to format content with proper line breaks
  const formatQuotedContent = (content: string) => {
    if (!content) return '';
    const parsedContent = parseHtmlEntities(content);
    const lines = parsedContent.split('\n');
    return lines.join('\n');
  };

  const quotedMediaInfo = useMemo(() => getMediaInfo(quotedPost), [quotedPost]);

  // Add defensive checks for undefined author
  const author = quotedPost?.author;
  const userName = author?.full_name || "Unknown User";
  const userProfileImage = author?.profile_image || "/assets/placeholder.jpg";

  return (
    <div className="border border-gray-600 rounded-lg p-3 my-3 bg-gray-800/50">
      <div className="flex items-start space-x-2 mb-2">
        <img
          src={userProfileImage}
          alt={userName}
          className="w-10 h-10 rounded-full"
          onError={(e) => {
            e.currentTarget.src = "/assets/placeholder.jpg";
          }}
        />
        <div>
          <div className="flex flex-col space-y-1">
            <span className="font-medium text-white text-sm">{userName}</span>
            <span className="text-gray-400 text-sm">LinkedIn</span>
          </div>
        </div>
      </div>

      <div className="mb-2">
        <div className="text-white text-sm leading-relaxed whitespace-pre-line">
          <ProcessedText text={formatQuotedContent(quotedPost.text)} platform="LINKEDIN" />
        </div>
      </div>

      {(quotedPost.media_urls && quotedPost.media_urls.length > 0) || (quotedPost.media && quotedPost.media.images && quotedPost.media.images.length > 0) ? (
        <div className="mb-2">
          <MediaRenderer
            mediaInfo={quotedMediaInfo}
            maxHeight="200px"
            className="w-full"
          />
        </div>
      ) : null}
    </div>
  );
};

const ListView: React.FC<ListViewProps> = ({ reels, loading, initialLoading, isBlurred, onLoadMore, hasMore, onNoteUpdate, onCategoryUpdate, onPostDelete, onPostArchive, favouriteStates, onFavouriteToggle, categories, showFullCaption = false, selectionMode = false, selectedPostIds, onToggleSelect }) => {
  const [openThreadPopupId, setOpenThreadPopupId] = useState<string | null>(null);
  const [openFullPostPopupId, setOpenFullPostPopupId] = useState<string | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [openNoteModalId, setOpenNoteModalId] = useState<string | null>(null);
  const [openDeleteDialogId, setOpenDeleteDialogId] = useState<string | null>(null);
  const [openCategoryPopupId, setOpenCategoryPopupId] = useState<string | null>(null);
  const [noteUpdateTrigger, setNoteUpdateTrigger] = useState(0);
  const [globalLoading, setGlobalLoading] = useState({ isVisible: false, message: '' });
  const [favoriteLoadingStates, setFavoriteLoadingStates] = useState<{ [key: string]: boolean }>({});
  const [optimisticFavoriteStates, setOptimisticFavoriteStates] = useState<{ [key: string]: boolean | null }>({});
  const observer = useRef<IntersectionObserver | null>(null);
  const loadingRef = useRef(loading);
  const hasMoreRef = useRef(hasMore);
  const menuRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  // Handle global loading state changes
  const handleGlobalLoadingChange = (isLoading: boolean, message?: string) => {
    setGlobalLoading({ isVisible: isLoading, message: message || '' });
  };

  // Use the unified post actions hook
  const {
    handleDeletePost,
    handleConfirmArchive,
    handleArchivePost,
    handleSharePost,
    handleFavouriteToggle
  } = usePostActions({
    onPostDelete,
    onPostArchive,
    onFavouriteToggle,
    favouriteStates,
    onGlobalLoadingChange: handleGlobalLoadingChange
  });

  // Update refs when props change
  useEffect(() => {
    loadingRef.current = loading;
    hasMoreRef.current = hasMore;
  }, [loading, hasMore]);

  // Setup intersection observer for 70% scroll detection
  const lastReelElementRef = useCallback((node: HTMLDivElement | null) => {
    if (observer.current) {
      observer.current.disconnect();
    }

    if (loading || isBlurred || !hasMore) {
      return;
    }

    observer.current = new IntersectionObserver(entries => {
      const entry = entries[0];

      if (entry.isIntersecting && hasMoreRef.current && !loadingRef.current) {
        onLoadMore();
      }
    }, { threshold: 0.3 }); // Trigger when 30% of the element is visible (70% scrolled)

    if (node) {
      observer.current.observe(node);
    }
  }, [onLoadMore, isBlurred, loading, hasMore, reels.length]);

  // Cleanup observer on unmount
  useEffect(() => {
    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, []);



  // Handle clicking outside the menu to close it
  useEffect(() => {
    const currentOpenMenuId = openMenuId;
    const currentMenuRefs = menuRefs.current;
    const handleClickOutside = (event: MouseEvent) => {
      if (currentOpenMenuId) {
        const menuRef = currentMenuRefs[currentOpenMenuId];
        if (menuRef && !menuRef.contains(event.target as Node)) {
          setOpenMenuId(null);
        }
      }
    };

    if (currentOpenMenuId) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openMenuId, menuRefs]);



  const handleMenuClick = (e: React.MouseEvent, reelId: string) => {
    e.stopPropagation();
    setOpenMenuId(openMenuId === reelId ? null : reelId);
  };

  const handleViewOriginal = (e: React.MouseEvent, reel: Post) => {
    e.stopPropagation();
    setOpenMenuId(null);
    handlePostClickAction(reel);
  };

  const handleNoteClick = (e: React.MouseEvent, reel: Post) => {
    e.stopPropagation();
    setOpenNoteModalId(reel.id.toString());
  };

  const handleCategoryClick = (e: React.MouseEvent, reel: Post) => {
    e.stopPropagation();
    setOpenCategoryPopupId(reel.id.toString());
  };

  const handleCategoryUpdate = (postId: number, category?: any) => {
    // Call the parent callback to update the reels array
    if (onCategoryUpdate) {
      onCategoryUpdate(postId, category);
    }

    // Track category update
    if (category) {
      trackPostCategorized(postId.toString(), category.id, category.category_display_value);
    }

    // Close the popup
    setOpenCategoryPopupId(null);
  };

  const handleDeletePostClick = (e: React.MouseEvent, reel: Post) => {
    e.stopPropagation();
    setOpenMenuId(null);
    setOpenDeleteDialogId(reel.id.toString());
  };

  const handleConfirmDelete = async (reelId: number) => {
    const reel = reels.find(r => r.id === reelId);
    if (reel) {
      await handleDeletePost(reelId, reel.platform);
    }
  };

  const handleConfirmArchiveClick = async (reelId: number) => {
    const reel = reels.find(r => r.id === reelId);
    if (reel) {
      await handleConfirmArchive(reelId, reel.platform);
    }
  };

  const handleArchivePostClick = async (e: React.MouseEvent, reel: Post) => {
    e.stopPropagation();
    setOpenMenuId(null);
    await handleArchivePost(reel);
  };

  const handleFavouriteToggleClick = async (e: React.MouseEvent, reel: Post) => {
    e.stopPropagation();
    setOpenMenuId(null);
    
    const reelId = reel.id.toString();
    
    // Prevent multiple clicks
    if (favoriteLoadingStates[reelId]) return;
    
    // Get current state and toggle optimistically for immediate visual feedback
    const currentState = optimisticFavoriteStates[reelId] !== undefined && optimisticFavoriteStates[reelId] !== null
      ? optimisticFavoriteStates[reelId]
      : (favouriteStates?.[reelId] ?? reel.is_favorite ?? false);
    const newState = !currentState;
    
    // Update optimistic state immediately for instant visual feedback
    setOptimisticFavoriteStates(prev => ({ ...prev, [reelId]: newState }));
    setFavoriteLoadingStates(prev => ({ ...prev, [reelId]: true }));
    
    try {
      await handleFavouriteToggle(reel);
      // Clear optimistic state on success (let props take over)
      setOptimisticFavoriteStates(prev => ({ ...prev, [reelId]: null }));
    } catch (error) {
      // Revert optimistic state on error
      setOptimisticFavoriteStates(prev => ({ ...prev, [reelId]: currentState }));
      console.error('Failed to toggle favorite:', error);
    } finally {
      setFavoriteLoadingStates(prev => ({ ...prev, [reelId]: false }));
    }
  };

  const handleSharePostClick = async (e: React.MouseEvent, reel: Post) => {
    e.stopPropagation();
    setOpenMenuId(null);
    await handleSharePost(reel);
  };

  return (
    <div className="text-white px-2 relative">
      {initialLoading ? (
        <div className="flex justify-center items-center h-[calc(100vh-200px)]">
          <Loader size="lg" showText text="Loading your reels..." />
        </div>
      ) : (
        <div className="max-w-5xl mx-auto">
          <div className="space-y-6 mt-4">
            {reels.map((reel, index) => {
              // Get post display data using unified utility (no truncation when showFullCaption)
              const postData = getPostDisplayData(reel, showFullCaption ? 100000 : 512);
              let postDataForMedia;
              if (reel.platform === 'X') {
                postDataForMedia = reel.x_post;
              } else if (reel.platform === 'LINKEDIN') {
                postDataForMedia = reel.linkedin_post;
              } else if (reel.platform === 'TIKTOK') {
                postDataForMedia = reel.tiktok_post;
              } else if (reel.platform === 'REDDIT') {
                postDataForMedia = reel.reddit_post;
              } else if (reel.platform === 'CHROME_BOOKMARK') {
                postDataForMedia = reel.chrome_bookmark_post;
              } else {
                postDataForMedia = reel.instagram_post;
              }
              const mediaInfo = getMediaInfo(postDataForMedia);

              const isRowSelected = !!selectedPostIds?.has(reel.id);
              return (
                <div
                  key={`${reel.id}-${noteUpdateTrigger}`}
                  className={`bg-slate-800/50 rounded-lg border p-4 sm:p-6 transition-colors ${isRowSelected ? 'border-purple-500 ring-2 ring-purple-500/60' : 'border-gray-700'} ${selectionMode ? 'cursor-pointer select-none' : ''}`}
                  ref={index === reels.length - 1 ? lastReelElementRef : null}
                  data-last-element={index === reels.length - 1}
                  data-reel-id={reel.id}
                  data-index={index}
                  onClick={selectionMode ? (e) => onToggleSelect?.(reel.id, e.shiftKey) : undefined}
                >
                  <div className="flex flex-col md:flex-row w-full">
                    {/* Left side - Content (full on mobile, 68% on desktop) */}
                    <div className={postData.hasMedia ? "w-full md:w-[68%] min-w-0" : "w-full min-w-0"}>
                      {/* Author info and category */}
                      <div className="mb-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center space-x-3 min-w-0 flex-1">
                            {reel.platform === 'CHROME_BOOKMARK' ? (
                              <FaviconImage
                                src={postData.authorInfo.profileImage ?? ''}
                                alt={postData.authorInfo.name}
                              />
                            ) : (
                              <ProfileImage
                                url={postData.authorInfo.profileImage}
                                alt={postData.authorInfo.name}
                                postId={reel.id}
                                platform={reel.platform}
                                className="w-12 h-12 rounded-full flex-shrink-0"
                              />
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-col">
                                <span className="font-semibold text-white text-base truncate">
                                  {postData.authorInfo.name}
                                </span>
                                {reel.platform !== 'CHROME_BOOKMARK' && (
                                  <span className="text-gray-400 text-sm truncate">
                                    {postData.authorInfo.handle
                                      ? postData.authorInfo.handle
                                      : `@${postData.authorInfo.username}`}
                                  </span>
                                )}
                              </div>
                              <p className="text-gray-400 text-xs mt-1 italic">
                                {postData.postTime && `Posted ${postData.postTime} ago`}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2 flex-shrink-0 ml-2">
                            {selectionMode ? (
                              // Visual only — whole row handles the click (incl. shift-range).
                              <div className="flex items-center p-2">
                                <Checkbox
                                  checked={isRowSelected}
                                  className="pointer-events-none"
                                  aria-label={isRowSelected ? `Deselect post ${reel.id}` : `Select post ${reel.id}`}
                                />
                              </div>
                            ) : (
                            <>
                            {/* Category - desktop only in this row */}
                            {reel.category !== undefined && reel.category !== null && (reel.category.category_enum !== 'others' || reel.category.label) ? (
                              <button
                                onClick={(e) => handleCategoryClick(e, reel)}
                                className="hidden md:inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium max-w-[120px] truncate shrink-0 [&>svg]:size-3 gap-1 focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden hover:bg-purple-500/30 bg-purple-500/20 text-purple-300 border-purple-500/30 cursor-pointer"
                                title="Change Category"
                              >
                                <span className="truncate">
                                  {reel.category.category_enum === 'others'
                                    ? reel.category.label
                                    : reel.category.category_display_value}
                                </span>
                                <ChevronDown className="w-3 h-3 flex-shrink-0" />
                              </button>
                            ) : (
                              <div
                                className="flex items-center p-2 rounded-lg border border-transparent hover:border-gray-600 cursor-pointer"
                                onClick={(e) => handleCategoryClick(e, reel)}
                                title='Add Category'
                              >
                                <Tag className="w-4 h-4 flex-shrink-0 text-gray-400" />
                              </div>
                            )}
                            {/* Favourite Button */}
                            <button
                              onClick={(e) => handleFavouriteToggleClick(e, reel)}
                              disabled={favoriteLoadingStates[reel.id.toString()]}
                              className="flex bg-transparent items-center justify-center p-2 rounded-lg border border-transparent hover:border-gray-600 cursor-pointer focus:outline-none focus:ring-0 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                              title={(() => {
                                const reelId = reel.id.toString();
                                const isFavorite = optimisticFavoriteStates[reelId] !== undefined && optimisticFavoriteStates[reelId] !== null
                                  ? optimisticFavoriteStates[reelId]
                                  : getFavouriteState(reel, favouriteStates);
                                return isFavorite ? 'Unfavourite' : 'Favourite';
                              })()}
                            >
                              <Star
                                className={`w-4 h-4 ${(() => {
                                  const reelId = reel.id.toString();
                                  const isFavorite = optimisticFavoriteStates[reelId] !== undefined && optimisticFavoriteStates[reelId] !== null
                                    ? optimisticFavoriteStates[reelId]
                                    : getFavouriteState(reel, favouriteStates);
                                  return isFavorite ? 'fill-yellow-400 text-yellow-400' : 'text-gray-400 hover:text-yellow-400';
                                })()}`}
                              />
                            </button>

                            <div className="relative" ref={(el) => menuRefs.current[reel.id.toString()] = el}>
                              <div
                                className="flex items-center p-2 rounded-lg border border-transparent hover:border-gray-600 cursor-pointer"
                                onClick={(e) => handleMenuClick(e, reel.id.toString())}
                              >
                                <MoreHorizontal className="w-4 h-4 text-gray-400" />
                              </div>

                              {/* Dropdown Menu */}
                              {openMenuId === reel.id.toString() && (
                                <div className="absolute right-0 top-full mt-1 bg-slate-700 border border-gray-600 rounded-lg shadow-lg z-10 min-w-[160px]">
                                  <div className="py-1">
                                    <button
                                      className="flex items-center bg-transparent w-full px-4 py-2 text-sm text-gray-300 hover:bg-gray-600 hover:text-white transition-colors"
                                      onClick={(e) => handleViewOriginal(e, reel)}
                                    >
                                      <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                      </svg>
                                      View Original
                                    </button>
                                    <button
                                      className="flex items-center bg-transparent w-full px-4 py-2 text-sm text-gray-300 hover:bg-gray-600 hover:text-white transition-colors"
                                      onClick={(e) => handleSharePostClick(e, reel)}
                                    >
                                      <Share className="w-4 h-4 mr-3" />
                                      Share
                                    </button>
                                    <button
                                      className="flex items-center bg-transparent w-full px-4 py-2 text-sm text-gray-300 hover:bg-gray-600 hover:text-white transition-colors"
                                      onClick={(e) => handleArchivePostClick(e, reel)}
                                    >
                                      {(reel.is_archived) ? (
                                        <>
                                          <ArchiveRestore className="w-4 h-4 mr-3" />
                                          Unarchive
                                        </>
                                      ) : (
                                        <>
                                          <Archive className="w-4 h-4 mr-3" />
                                          Archive
                                        </>
                                      )}
                                    </button>
                                    <button
                                      className="flex items-center bg-transparent w-full px-4 py-2 text-sm text-red-400 hover:bg-red-900/20 hover:text-red-300 transition-colors"
                                      onClick={(e) => handleDeletePostClick(e, reel)}
                                    >
                                      <Trash2 className="w-4 h-4 mr-3" />
                                      Delete
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                            </>
                            )}
                          </div>
                        </div>

                        {/* Category badge — mobile only, second line, only when a label is set */}
                        {!selectionMode && reel.category !== undefined && reel.category !== null && (reel.category.category_enum !== 'others' || reel.category.label) && (
                          <div className="md:hidden mt-2">
                            <button
                              onClick={(e) => handleCategoryClick(e, reel)}
                              className="inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium gap-1 hover:bg-purple-500/30 bg-purple-500/20 text-purple-300 border-purple-500/30 cursor-pointer"
                              title="Change Category"
                            >
                              <span className="truncate max-w-[180px]">
                                {reel.category.category_enum === 'others'
                                  ? reel.category.label
                                  : reel.category.category_display_value}
                              </span>
                              <ChevronDown className="w-3 h-3 flex-shrink-0" />
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Mobile-only: media shown between author and text */}
                      {postData.hasMedia && (
                        <div className="md:hidden mb-4">
                          <div className="w-full rounded-lg overflow-hidden bg-transparent">
                            <MediaRenderer
                              mediaInfo={mediaInfo}
                              maxHeight="300px"
                              objectFit="cover"
                              objectPosition="center"
                              className="w-full h-auto"
                              showPlayButton={true}
                              post={reel}
                            />
                          </div>
                        </div>
                      )}

                      {/* Post content */}
                      <div className="mb-4">
                        {reel.platform === 'REDDIT' && reel.reddit_post?.title ? (
                          <div className="font-bold text-white text-base mb-2 leading-snug">
                            {reel.reddit_post.title}
                          </div>
                        ) : null}
                        <p className={`text-white text-base leading-relaxed whitespace-pre-line overflow-hidden ${showFullCaption ? '' : 'line-clamp-8'}`}>
                          {postData.truncatedText}
                        </p>
                      </div>

                      {/* Chrome Bookmark — link preview card + folder breadcrumb */}
                      {reel.platform === 'CHROME_BOOKMARK' && reel.chrome_bookmark_post && (
                        <div className="mb-4 space-y-2">
                          <a
                            href={reel.chrome_bookmark_post.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block rounded-xl overflow-hidden border border-gray-600/50 bg-gray-700/40 hover:bg-gray-700/70 transition-colors"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {reel.chrome_bookmark_post.og_image_url && (
                              <img
                                src={reel.chrome_bookmark_post.og_image_url}
                                alt={reel.chrome_bookmark_post.og_title || reel.chrome_bookmark_post.title || ''}
                                className="w-full h-36 object-cover"
                                onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                              />
                            )}
                            <div className="px-3 py-2">
                              {(reel.chrome_bookmark_post.og_title || reel.chrome_bookmark_post.title) && (
                                <p className="text-sm font-medium text-white line-clamp-2 leading-snug">
                                  {reel.chrome_bookmark_post.og_title || reel.chrome_bookmark_post.title}
                                </p>
                              )}
                              {reel.chrome_bookmark_post.og_description && (
                                <p className="text-xs text-gray-400 mt-0.5 line-clamp-2 leading-snug">
                                  {reel.chrome_bookmark_post.og_description}
                                </p>
                              )}
                              <p className="text-xs text-gray-500 mt-1 truncate">{reel.chrome_bookmark_post.url}</p>
                            </div>
                          </a>
                          {reel.chrome_bookmark_post.folder_path && reel.chrome_bookmark_post.folder_path.length > 0 && (
                            <div className="flex items-center gap-1 text-xs text-gray-500 truncate">
                              <svg className="w-3 h-3 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
                              </svg>
                              <span className="truncate">{reel.chrome_bookmark_post.folder_path.join(' › ')}</span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Hashtags */}
                      {postData.hashtags.length > 0 && (
                        <div className="mb-4">
                          <HashtagDisplay hashtags={postData.hashtags} />
                        </div>
                      )}

                      {/* Note Display - only show when note exists */}
                      {reel.note && (
                        <div className="mb-4 p-4 bg-gray-700/50 border border-orange-500/30 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center space-x-2">
                              <FileText className="w-4 h-4 text-orange-400" />
                              <span className="text-orange-400 text-sm font-medium">Your Note</span>
                            </div>
                            <div
                              onClick={(e) => handleNoteClick(e, reel)}
                              className="text-orange-400 hover:text-orange-300 p-2 hover:bg-orange-300/20 rounded-lg transition-colors bg-transparent cursor-pointer"
                              title="Edit Note"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3 w-3"><path d="M12 20h9"></path><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"></path></svg>
                            </div>
                          </div>
                          <div className="text-white text-sm leading-relaxed">
                            {reel.note}
                          </div>
                        </div>
                      )}

                      {/* Quoted Tweet - only for X posts */}
                      {reel.platform === 'X' && reel.x_post?.quoted_tweet && (
                        <QuotedTweet quotedTweet={reel.x_post.quoted_tweet} />
                      )}

                      {/* Quoted Post - for LinkedIn posts */}
                      {reel.platform === 'LINKEDIN' && reel.linkedin_post?.quoted_post && (
                        <QuotedLinkedInPost quotedPost={reel.linkedin_post.quoted_post} />
                      )}

                      {/* LinkedIn Poll Component */}
                      {reel.platform === 'LINKEDIN' && reel.linkedin_post?.poll_component && (
                        <LinkedInPollComponent pollComponent={reel.linkedin_post.poll_component} />
                      )}

                      {/* LinkedIn Article Component */}
                      {reel.platform === 'LINKEDIN' && reel.linkedin_post?.article_component && (
                        <LinkedInArticleComponent articleComponent={reel.linkedin_post.article_component} />
                      )}

                      {/* Action buttons row - Read Thread, Read full post, or Add Note */}
                      <div className="mb-4">
                        {postData.isThread ? (
                          <div className="flex items-center space-x-3">
                            <div
                              className="flex items-center space-x-2 bg-[#1d2c4a] text-[#6b8ce3] hover:bg-[#243660] px-3 py-1 sm:px-4 sm:py-1.5 rounded-full cursor-pointer text-sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenThreadPopupId(reel.id.toString());
                              }}
                            >
                              <span>Read Thread • {reel.x_post?.tweet_threads?.length || 0} posts</span>
                              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </div>
                            {/* Note Button - only show when no note exists */}
                            {!reel.note && (
                              <button
                                onClick={(e) => handleNoteClick(e, reel)}
                                className="flex items-center space-x-2 px-3 py-1 sm:px-4 sm:py-1.5 rounded-full text-sm transition-colors bg-[#1d2c4a] text-[#6b8ce3] hover:bg-[#243660]"
                              >
                                <FileText className="w-4 h-4" />
                                <span>Add Note</span>
                              </button>
                            )}
                          </div>
                        ) : postData.isTruncated ? (
                          <div className="flex items-center space-x-3">
                            <button
                              className="flex items-center space-x-2 bg-[#1d2c4a] text-[#6b8ce3] hover:bg-[#243660] px-3 py-1 sm:px-4 sm:py-1.5 rounded-full cursor-pointer text-sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenFullPostPopupId(reel.id.toString());
                              }}
                            >
                              <span>Read full post</span>
                              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </button>
                            {/* Note Button - only show when no note exists */}
                            {!reel.note && (
                              <button
                                onClick={(e) => handleNoteClick(e, reel)}
                                className="flex items-center space-x-2 px-3 py-1 sm:px-4 sm:py-1.5 rounded-full text-sm transition-colors bg-[#1d2c4a] text-[#6b8ce3] hover:bg-[#243660]"
                              >
                                <FileText className="w-4 h-4" />
                                <span>Add Note</span>
                              </button>
                            )}
                          </div>
                        ) : (
                          /* Note Button - only show when no note exists and no other buttons are available */
                          !reel.note && (
                            <button
                              onClick={(e) => handleNoteClick(e, reel)}
                              className="flex items-center space-x-2 px-3 py-1 sm:px-4 sm:py-1.5 rounded-full text-sm transition-colors bg-[#1d2c4a] text-[#6b8ce3] hover:bg-[#243660]"
                            >
                              <FileText className="w-4 h-4" />
                              <span>Add Note</span>
                            </button>
                          )
                        )}
                      </div>

                      {/* Engagement metrics */}
                      {reel.platform == "X" && <div className="flex items-center justify-between text-gray-400 text-sm">
                        <div className="flex items-center space-x-6">
                          <div className="flex items-center space-x-1">
                            <Heart className="w-4 h-4" />
                            <span>{formatNumber(postData.metrics.likes)}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Repeat className="w-4 h-4" />
                            <span>{formatNumber(postData.metrics.retweets)}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <MessageCircle className="w-4 h-4" />
                            <span>{formatNumber(postData.metrics.replies)}</span>
                          </div>
                        </div>
                      </div>}

                      {/* LinkedIn engagement metrics */}
                      {reel.platform === "LINKEDIN" && <div className="flex items-center justify-between text-gray-400 text-sm">
                        <div className="flex items-center space-x-6">
                          <div className="flex items-center space-x-1">
                            <Heart className="w-4 h-4" />
                            <span>{formatNumber(postData.metrics.likes)}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Share2 className="w-4 h-4" />
                            <span>{formatNumber(postData.metrics.shares)}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <MessageCircle className="w-4 h-4" />
                            <span>{formatNumber(postData.metrics.comments)}</span>
                          </div>
                        </div>
                      </div>}

                      {/* TikTok engagement metrics */}
                      {reel.platform === "TIKTOK" && <div className="flex items-center justify-between text-gray-400 text-sm">
                        <div className="flex items-center space-x-6">
                          {postData.metrics.likes !== undefined && postData.metrics.likes > 0 && (
                            <div className="flex items-center space-x-1">
                              <Heart className="w-4 h-4" />
                              <span>{formatNumber(postData.metrics.likes)}</span>
                            </div>
                          )}
                          {'play' in postData.metrics && postData.metrics.play !== undefined && postData.metrics.play > 0 && (
                            <div className="flex items-center space-x-1">
                              <Eye className="w-4 h-4" />
                              <span>{formatNumber(postData.metrics.play)}</span>
                            </div>
                          )}
                          {'comments' in postData.metrics && postData.metrics.comments !== undefined && postData.metrics.comments > 0 && (
                            <div className="flex items-center space-x-1">
                              <MessageCircle className="w-4 h-4" />
                              <span>{formatNumber(postData.metrics.comments)}</span>
                            </div>
                          )}
                          {'shares' in postData.metrics && postData.metrics.shares !== undefined && postData.metrics.shares > 0 && (
                            <div className="flex items-center space-x-1">
                              <Share2 className="w-4 h-4" />
                              <span>{formatNumber(postData.metrics.shares)}</span>
                            </div>
                          )}
                        </div>
                      </div>}

                      {/* Reddit engagement metrics */}
                      {reel.platform === "REDDIT" && <div className="flex items-center justify-between text-gray-400 text-sm">
                        <div className="flex items-center space-x-6">
                          {postData.metrics.likes !== undefined && postData.metrics.likes > 0 && (
                            <div className="flex items-center space-x-1">
                              <ArrowBigUp className="w-4 h-4" />
                              <span>{formatNumber(postData.metrics.likes)}</span>
                            </div>
                          )}
                          {'comments' in postData.metrics && postData.metrics.comments !== undefined && postData.metrics.comments > 0 && (
                            <div className="flex items-center space-x-1">
                              <MessageCircle className="w-4 h-4" />
                              <span>{formatNumber(postData.metrics.comments)}</span>
                            </div>
                          )}
                        </div>
                      </div>}
                    </div>

                    {/* Middle space with centered divider (desktop only) */}
                    {postData.hasMedia && (
                      <div className="hidden md:flex w-[4%] items-center justify-center">
                        <div className="w-px h-full bg-gray-600/20"></div>
                      </div>
                    )}

                    {/* Right side - Media (desktop only; mobile shown between author and content) */}
                    {postData.hasMedia && (
                      <div className="hidden md:flex w-[28%] justify-center">
                        <div className="w-full rounded-lg overflow-hidden bg-transparent flex items-center justify-center relative">
                          <MediaRenderer
                            mediaInfo={mediaInfo}
                            maxHeight="400px"
                            maxWidth="300px"
                            objectFit="cover"
                            objectPosition="center"
                            className="w-full h-auto"
                            showPlayButton={true}
                            post={reel}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Loading indicator at bottom */}
          <div className="py-4 flex justify-center">
            {loading && !initialLoading && (
              <Loader size="md" showText text="Loading more reels..." />
            )}
          </div>
        </div>
      )
      }
      {
        isBlurred && !initialLoading && (
          <div className="absolute inset-0 bg-white bg-opacity-0 backdrop-blur-md z-1" />
        )
      }

      {/* Thread Popup */}
      {openThreadPopupId && (() => {
        const selectedReel = reels.find(reel => reel.id.toString() === openThreadPopupId);
        if (selectedReel && selectedReel.platform === 'X' && selectedReel.x_post?.tweet_threads) {
          return (
            <ThreadPopup
              author={selectedReel.x_post.author}
              originalTweet={selectedReel.x_post}
              threads={selectedReel.x_post.tweet_threads}
              onClose={() => setOpenThreadPopupId(null)}
            />
          );
        }
        return null;
      })()}

      {/* Full Post Popup */}
      {openFullPostPopupId && (() => {
        const selectedReel = reels.find(reel => reel.id.toString() === openFullPostPopupId);
        if (selectedReel) {
          const postDataForPopup = getPostDisplayData(selectedReel, 512);
          let postData;
          if (selectedReel.platform === 'X') {
            postData = selectedReel.x_post;
          } else if (selectedReel.platform === 'LINKEDIN') {
            postData = selectedReel.linkedin_post;
          } else if (selectedReel.platform === 'TIKTOK') {
            postData = selectedReel.tiktok_post;
          } else if (selectedReel.platform === 'REDDIT') {
            postData = selectedReel.reddit_post;
          } else if (selectedReel.platform === 'CHROME_BOOKMARK') {
            postData = selectedReel.chrome_bookmark_post;
          } else {
            postData = selectedReel.instagram_post;
          }

          return (
            <FullPostPopup
              post={postData}
              author={postDataForPopup.authorInfo}
              platform={selectedReel.platform}
              postId={selectedReel.id}
              onClose={() => setOpenFullPostPopupId(null)}
            />
          );
        }
        return null;
      })()}

      {/* Note Modal */}
      {openNoteModalId && (() => {
        const selectedReel = reels.find(reel => reel.id.toString() === openNoteModalId);
        if (selectedReel) {
          return (
            <NoteModal
              post={selectedReel}
              isOpen={true}
              onClose={() => setOpenNoteModalId(null)}
              onNoteUpdate={(note: string) => {
                if (onNoteUpdate) {
                  onNoteUpdate(selectedReel.id, note);
                }
                // Update local state to reflect changes immediately
                selectedReel.note = note;
                // Force re-render by updating the trigger
                setNoteUpdateTrigger(prev => prev + 1);
                setOpenNoteModalId(null);
              }}
            />
          );
        }
        return null;
      })()}

      {/* Delete Confirmation Dialog */}
      {openDeleteDialogId && (() => {
        const selectedReel = reels.find(reel => reel.id.toString() === openDeleteDialogId);
        if (selectedReel) {
          return (
            <DeleteConfirmDialog
              isOpen={true}
              onClose={() => setOpenDeleteDialogId(null)}
              onConfirm={() => handleConfirmDelete(selectedReel.id)}
              onArchive={() => handleConfirmArchiveClick(selectedReel.id)}
              isArchived={selectedReel.is_archived}
            />
          );
        }
        return null;
      })()}

      {/* Category Change Popup */}
      {openCategoryPopupId && (() => {
        const selectedReel = reels.find(reel => reel.id.toString() === openCategoryPopupId);
        if (selectedReel) {
          return (
            <CategoryChangePopup
              post={selectedReel}
              isOpen={true}
              onClose={() => setOpenCategoryPopupId(null)}
              onCategoryUpdate={handleCategoryUpdate}
              categories={categories}
            />
          );
        }
        return null;
      })()}

      {/* Global Loader for Archive/Delete Operations */}
      <GlobalLoader 
        isVisible={globalLoading.isVisible} 
        message={globalLoading.message} 
      />

    </div >

  );
};

export default ListView; 